
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, DollarSign, Package, Users } from "lucide-react";
import { useSalesAnalytics } from "@/hooks/useSalesAnalytics";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";

const SalesPerformanceChart = () => {
  const { data, isLoading } = useSalesAnalytics();

  if (isLoading) {
    return (
      <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const monthlyData = Object.entries(data?.monthlySales || {}).map(([month, salesData]: [string, any]) => ({
    month,
    revenue: salesData.total,
    bookings: salesData.count,
    average: salesData.total / salesData.count
  }));

  const kpiData = [
    {
      title: "إجمالي الإيرادات",
      value: `${(data?.totalRevenue || 0).toLocaleString('ar-SA')} ر.س`,
      icon: DollarSign,
      color: "from-green-600 to-green-500",
      bgColor: "bg-green-100",
      iconColor: "text-green-600"
    },
    {
      title: "إجمالي الحجوزات",
      value: data?.totalBookings || 0,
      icon: Package,
      color: "from-blue-600 to-blue-500",
      bgColor: "bg-blue-100",
      iconColor: "text-blue-600"
    },
    {
      title: "متوسط قيمة الطلب",
      value: `${(data?.averageOrderValue || 0).toLocaleString('ar-SA')} ر.س`,
      icon: TrendingUp,
      color: "from-purple-600 to-purple-500",
      bgColor: "bg-purple-100",
      iconColor: "text-purple-600"
    },
    {
      title: "أفضل موظف مبيعات",
      value: data?.topEmployees?.[0]?.name || "لا يوجد",
      icon: Users,
      color: "from-orange-600 to-orange-500",
      bgColor: "bg-orange-100",
      iconColor: "text-orange-600"
    }
  ];

  return (
    <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
      <CardHeader>
        <CardTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-600" />
          أداء المبيعات
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* مؤشرات الأداء الرئيسية */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {kpiData.map((kpi, index) => (
            <div key={index} className="p-4 bg-gradient-to-br from-white to-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <div className={`w-10 h-10 ${kpi.bgColor} rounded-lg flex items-center justify-center`}>
                  <kpi.icon className={`w-5 h-5 ${kpi.iconColor}`} />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-800 mb-1">{kpi.value}</p>
              <p className="text-sm text-slate-600">{kpi.title}</p>
            </div>
          ))}
        </div>

        {/* رسم بياني شهري */}
        {monthlyData.length > 0 && (
          <div className="h-80">
            <h3 className="text-lg font-semibold text-slate-700 mb-4">الأداء الشهري</h3>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [
                    name === 'revenue' ? `${value.toLocaleString('ar-SA')} ر.س` : value,
                    name === 'revenue' ? 'الإيرادات' : name === 'bookings' ? 'الحجوزات' : 'المتوسط'
                  ]}
                />
                <Bar dataKey="revenue" fill="#10b981" name="revenue" />
                <Bar dataKey="bookings" fill="#3b82f6" name="bookings" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SalesPerformanceChart;
